import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Typography,
  Stack,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import SchoolIcon from '@mui/icons-material/School'
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { progressApi, memorizationApi } from '../lib/api'
import { quranApi } from '../lib/quranApi'
import { useReaderState } from '../hooks/useReaderState'
import { usePrerenderReady } from '../hooks/usePrerenderReady'
import { SITE_ORIGIN } from '../lib/seo'
import { LEARN_PATH, LEARN_PRACTICE_PATH } from '../lib/learnRoutes'
import { PopularSurahsSection } from '../components/PopularSurahsSection'
import { PopularAyahsSection } from '../components/PopularAyahsSection'
import { SurahIndexSection } from '../components/SurahIndexSection'
import type { ReadingProgress, MemorizationData } from '../types'

// Local display shape for the Continue-Reading hero. The backend route for
// cross-day sync is not yet implemented, so we read from localStorage and
// look up the surah name from the cached surahs list.
type HomeLastPosition = {
  surahNumber: number
  ayahNumber: number
  surahName: string
}

const HOME_TITLE = 'Qlearn: Read & listen to the Quran online with translation'
const HOME_DESCRIPTION =
  'Read and listen to the Holy Quran online in Arabic with English and French translations, verse-by-verse audio recitation, word-by-word study, memorization tools, and reading-progress tracking.'

function getGreetingKey(): string {
  const hour = new Date().getHours()
  if (hour < 6) return 'home.salam'
  if (hour < 12) return 'home.morning'
  if (hour < 17) return 'home.afternoon'
  if (hour < 21) return 'home.evening'
  return 'home.salam'
}

interface StatChipProps {
  value: number
  label: string
  color: string
  icon: ReactNode
  muted?: boolean
}

function StatChip({ value, label, color, icon, muted }: StatChipProps) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        opacity: muted ? 0.72 : 1,
        minWidth: 0,
      }}
    >
      <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, color, lineHeight: 1.1, fontSize: '1.125rem' }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
          {label}
        </Typography>
      </Box>
    </Stack>
  )
}

interface SideTaskProps {
  icon: ReactNode
  title: string
  meta?: string
  onClick: () => void
}

function SideTask({ icon, title, meta, onClick }: SideTaskProps) {
  return (
    <Card sx={{ flex: 1, minHeight: 0 }}>
      <CardActionArea
        onClick={onClick}
        sx={{
          height: '100%',
          p: 1.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          justifyContent: 'flex-start',
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(45,212,191,0.14)' : '#F0FDFA',
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{title}</Typography>
          {meta && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {meta}
            </Typography>
          )}
        </Box>
        <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
      </CardActionArea>
    </Card>
  )
}

export default function HomePage() {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { user, isAuthenticated } = useAuth()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [todayProgress, setTodayProgress] = useState<ReadingProgress | null>(null)
  const [lastPosition, setLastPosition] = useState<HomeLastPosition | null>(null)
  const [dueForReview, setDueForReview] = useState<MemorizationData[]>([])
  const { lastPosition: storedPosition } = useReaderState()

  usePrerenderReady(true)

  useEffect(() => {
    if (!storedPosition) {
      setLastPosition(null)
      return
    }
    let cancelled = false
    quranApi.listSurahs()
      .then((surahs) => {
        if (cancelled) return
        const surah = surahs.find((s) => s.number === storedPosition.surahNumber)
        setLastPosition({
          surahNumber: storedPosition.surahNumber,
          ayahNumber: storedPosition.ayahNumber,
          surahName: surah?.englishName ?? t('home.surahFallback', { number: storedPosition.surahNumber }),
        })
      })
      .catch(() => {
        if (cancelled) return
        setLastPosition({
          surahNumber: storedPosition.surahNumber,
          ayahNumber: storedPosition.ayahNumber,
          surahName: t('home.surahFallback', { number: storedPosition.surahNumber }),
        })
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedPosition])

  useEffect(() => {
    if (!isAuthenticated) return

    async function loadData() {
      try {
        const [progress, reviewVerses] = await Promise.all([
          progressApi.getToday(),
          memorizationApi.listVerses('reviewing', 50),
        ])
        setTodayProgress(progress)
        const todayUtc = new Date().toISOString().split('T')[0]
        setDueForReview(
          reviewVerses.filter((v) => {
            if (!v.nextReviewDate) return false
            return v.nextReviewDate.slice(0, 10) <= todayUtc
          })
        )
      } catch {
        // Silently fail on home page
      }
    }
    loadData()
  }, [isAuthenticated])

  const versesToday = todayProgress?.totalVersesRead || 0
  const currentStreak = user?.currentStreak ?? 0
  const muted = !isAuthenticated

  const continueHref = lastPosition
    ? `/reader/${lastPosition.surahNumber}?ayah=${lastPosition.ayahNumber}`
    : '/reader'
  const continueTitle = lastPosition ? lastPosition.surahName : t('reader.openTheQuran')
  const continueMeta = lastPosition
    ? `${t('reader.continueReading')} · ${t('reader.ayah')} ${lastPosition.ayahNumber}`
    : t('reader.startReading')
  const continueCta = lastPosition ? t('reader.continueReading') : t('reader.startReading')

  const streakChip = (
    <StatChip
      value={currentStreak}
      label={t('home.streak')}
      color={muted ? theme.palette.text.disabled : theme.palette.primary.main}
      icon={<LocalFireDepartmentIcon fontSize="small" />}
      muted={muted}
    />
  )
  const versesChip = (
    <StatChip
      value={versesToday}
      label={t('home.versesReadToday')}
      color={muted ? theme.palette.text.disabled : theme.palette.primary.main}
      icon={<MenuBookIcon fontSize="small" />}
      muted={muted}
    />
  )

  const continueCard = (
    <Card
      sx={{
        height: '100%',
        background: (th) =>
          `linear-gradient(135deg, ${alpha(th.palette.primary.main, th.palette.mode === 'dark' ? 0.22 : 0.10)} 0%, ${alpha(th.palette.primary.dark, th.palette.mode === 'dark' ? 0.18 : 0.06)} 100%)`,
        borderColor: (th) => alpha(th.palette.primary.main, 0.22),
      }}
    >
      <Box sx={{ p: { xs: 2, md: 2.5 }, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', flex: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <PlayArrowIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 0.06 }}>
              {t('home.tasksTitle')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, mt: 0.25 }}>
              {continueTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {continueMeta}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          color="primary"
          endIcon={<PlayArrowIcon />}
          onClick={() => navigate(continueHref)}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, fontWeight: 700 }}
        >
          {continueCta}
        </Button>
      </Box>
    </Card>
  )

  const sideTasks = (
    <Stack spacing={1.25} sx={{ height: '100%' }}>
      {isAuthenticated && dueForReview.length > 0 && (
        <SideTask
          icon={<SchoolIcon />}
          title={t('home.taskReview')}
          meta={t('home.taskReviewCount', { count: dueForReview.length })}
          onClick={() => navigate(LEARN_PATH)}
        />
      )}
      {isAuthenticated && (
        <SideTask
          icon={<SportsEsportsIcon />}
          title={t('home.taskGames')}
          onClick={() => navigate(LEARN_PRACTICE_PATH)}
        />
      )}
      {!isAuthenticated && (
        <Card sx={{ flex: 1 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('home.tagline')}
            </Typography>
          </Box>
        </Card>
      )}
    </Stack>
  )

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 0 },
        width: '100%',
        maxWidth: 960,
        mx: 'auto',
      }}
    >
      <Helmet>
        <title>{HOME_TITLE}</title>
        <meta name="description" content={HOME_DESCRIPTION} />
        <link rel="canonical" href={`${SITE_ORIGIN}/`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Qlearn" />
        <meta property="og:title" content={HOME_TITLE} />
        <meta property="og:description" content={HOME_DESCRIPTION} />
        <meta property="og:url" content={`${SITE_ORIGIN}/`} />
        <meta property="og:image" content={`${SITE_ORIGIN}/pwa-512.png`} />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={HOME_TITLE} />
        <meta name="twitter:description" content={HOME_DESCRIPTION} />
        <meta name="twitter:image" content={`${SITE_ORIGIN}/pwa-512.png`} />
      </Helmet>

      {/* Header: greeting left, compact Today chips right (desktop). */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 2 }}
        sx={{
          alignItems: { xs: 'stretch', md: 'flex-end' },
          justifyContent: 'space-between',
          mb: 2.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant={isDesktop ? 'h4' : 'h5'} sx={{ fontWeight: 700 }}>
            {t(getGreetingKey())}
            {user?.displayName ? `, ${user.displayName}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {new Date().toLocaleDateString(i18n.language, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {streakChip}
          {versesChip}
        </Stack>
      </Stack>

      {/* Main: Continue hero + side tasks (2-col on desktop). */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
          gap: 1.5,
          mb: 3,
          alignItems: 'stretch',
        }}
      >
        {continueCard}
        {sideTasks}
      </Box>

      <PopularSurahsSection limit={10} />
      <PopularAyahsSection limit={10} />
      {/* Crawlable index of all 114 surahs. Deliberately last: it is the
          longest block and matters most to crawlers, which read the whole
          document, while the interactive sections above stay above the fold
          for visitors. */}
      <SurahIndexSection />
    </Box>
  )
}


